import { useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";
import {
  buildCoverageLines,
  type CoverageLine,
} from "@/lib/policy-coverages";
import { formatPolicyPremium } from "./policy-status";
import { CoverageStatusBadge, statusForCoverage } from "./CoverageStatusBadge";
import { PhysicalDamageVehicleList } from "./PhysicalDamageVehicleList";
import { PhysicalDamageTierCard } from "./PhysicalDamageTierCard";
import { getPhysicalDamageTiers } from "@shared/physical-damage-tiers";
import { useReplaceCoverages } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { AddCoverageDialog } from "./AddCoverageDialog";
import type {
  PhysicalDamageVehicleSelection,
  Policy,
  PolicyCoverages,
  PolicyVehicle,
} from "@shared/schema";

interface Props {
  policy: Policy;
}

export function PolicyCoveragesTab({ policy }: Props) {
  const lines = buildCoverageLines(policy.coverages as PolicyCoverages | null);
  // CSR-953: when PD-covered vehicles span more than one deductible tier, the
  // single editable PD CoverageRow is replaced by N read-only tier cards
  // (OQ-2 option a). Tiers are derived, never stored.
  const pdTiers = getPhysicalDamageTiers({
    vehicles: policy.vehicles as PolicyVehicle[] | null,
    trailers: policy.trailers as PolicyVehicle[] | null,
    coverages: policy.coverages as PolicyCoverages | null,
    physicalDamageVehicles:
      policy.physicalDamageVehicles as PhysicalDamageVehicleSelection | null,
  });
  const splitPhysicalDamage = pdTiers.tiers.length > 1;
  const [addOpen, setAddOpen] = useState(false);
  const [editKey, setEditKey] = useState<string | null>(null);
  const [deleteKey, setDeleteKey] = useState<string | null>(null);
  const replace = useReplaceCoverages(policy.id);
  const { toast } = useToast();

  const editValues =
    editKey
      ? ((policy.coverages as Record<string, Record<string, unknown> | null> | null)?.[editKey] ?? null)
      : null;

  const handleConfirmDelete = () => {
    if (!deleteKey) return;
    // CSR-1910 — a coverage edit now PRESERVES untouched blocks, so removal must
    // be an explicit signal, not key omission. Soft-remove via `status:"removed"`
    // (the established model: carrier sync emits it, the COI excludes it, and the
    // card renders greyed "Removed"). Omitting the key would leave the block
    // untouched now that the server-side merge preserves absent blocks.
    const current = (policy.coverages as Record<string, unknown> | null) ?? {};
    const block = (current[deleteKey] as Record<string, unknown> | undefined) ?? {};
    const next = { ...current, [deleteKey]: { ...block, status: "removed" } };
    replace.mutate({ coverages: next }, {
      onSuccess: () => {
        toast({ title: "Coverage removed" });
        setDeleteKey(null);
      },
      onError: (err: unknown) => {
        const message =
          err instanceof Error ? err.message : "Failed to remove coverage";
        toast({
          title: "Failed to remove coverage",
          description: message,
          variant: "destructive",
        });
        setDeleteKey(null);
      },
    });
  };

  return (
    <div className="space-y-4" data-testid="policy-coverages">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm text-muted-foreground">
          Total:{" "}
          <span className="font-semibold text-emerald-600">
            {formatPolicyPremium(policy.premium)}
          </span>
        </p>
        <Button
          variant="outline"
          size="sm"
          className="gap-1.5 cursor-pointer"
          data-testid="policy-coverages-add"
          onClick={() => setAddOpen(true)}
        >
          <Plus className="w-4 h-4" /> Add Coverage
        </Button>
      </div>

      {lines.length === 0 ? (
        <div
          className="rounded-xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground"
          data-testid="policy-coverages-empty"
        >
          No coverage lines on this policy.
        </div>
      ) : (
        <div className="space-y-3">
          {lines.map((line) =>
            line.key === "physicalDamage" && splitPhysicalDamage ? (
              pdTiers.tiers.map((tier) => (
                <PhysicalDamageTierCard
                  key={`pd-tier-${tier.comprehensive ?? "none"}-${tier.collision ?? "none"}`}
                  tier={tier}
                  status={statusForCoverage(line.status, policy.status)}
                />
              ))
            ) : (
              <CoverageRow
                key={line.key}
                line={line}
                policy={policy}
                onEdit={setEditKey}
                onRemove={setDeleteKey}
              />
            ),
          )}
        </div>
      )}

      <AddCoverageDialog open={addOpen} onOpenChange={setAddOpen} policy={policy} />
      <AddCoverageDialog
        open={editKey !== null}
        onOpenChange={(o) => {
          if (!o) setEditKey(null);
        }}
        policy={policy}
        editKey={editKey ?? undefined}
        editValues={editValues ?? undefined}
      />

      <AlertDialog
        open={deleteKey !== null}
        onOpenChange={(o) => {
          if (!o) setDeleteKey(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove coverage?</AlertDialogTitle>
            <AlertDialogDescription>
              This marks the coverage as removed on the policy and forwards the
              change to the Data Center. It stays visible as a "Removed" card and
              is excluded from certificates. A future carrier sync may reactivate
              it if the carrier still reports it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="policy-coverage-remove-confirm"
              onClick={handleConfirmDelete}
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function CoverageRow({
  line,
  policy,
  onEdit,
  onRemove,
}: {
  line: CoverageLine;
  policy: Policy;
  onEdit: (key: string) => void;
  onRemove: (key: string) => void;
}) {
  const [showDetails, setShowDetails] = useState(false);
  const hasVehicleSection = !!line.hasVehicleSection;
  const hasDetails =
    (!!line.details && line.details.length > 0) || hasVehicleSection;
  // CSR-1978 — reconciled against the parent policy: a live line on a policy
  // that is no longer in force reads "Inactive", not "Active".
  const coverageStatus = statusForCoverage(line.status, policy.status);
  const isRemoved = coverageStatus === "removed";
  return (
    <div
      className={cn(
        "border border-border rounded-xl p-5",
        isRemoved ? "bg-muted/40" : "bg-card",
      )}
      data-testid={`policy-coverage-${line.key}`}
      data-coverage-status={coverageStatus}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-3">
            <p className={cn("font-semibold", isRemoved ? "text-muted-foreground" : "text-foreground")}>
              {line.name}
            </p>
            <CoverageStatusBadge status={coverageStatus} />
          </div>
          <CoverageMetrics line={line} />
        </div>
        <div className="flex flex-col items-end gap-2">
          {line.premiumOnRight && (
            <div
              className="text-right"
              data-testid={`policy-coverage-premium-${line.key}`}
            >
              <p className="text-xs text-muted-foreground mb-0.5">Premium</p>
              <p className="font-semibold text-emerald-600">{line.premium}</p>
            </div>
          )}
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-muted-foreground cursor-pointer"
              aria-label={`Edit ${line.name} coverage`}
              data-testid={`policy-coverage-edit-${line.key}`}
              onClick={() => onEdit(line.key)}
            >
              <Pencil className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-destructive cursor-pointer"
              aria-label={`Remove ${line.name} coverage`}
              data-testid={`policy-coverage-remove-${line.key}`}
              onClick={() => onRemove(line.key)}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
      {hasDetails && (
        <>
          <button
            type="button"
            onClick={() => setShowDetails((v) => !v)}
            className="mt-3 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
            data-testid={`policy-coverage-toggle-${line.key}`}
          >
            {showDetails ? "Hide details" : "Show details"}
          </button>
          {showDetails && (
            <>
              {line.details && line.details.length > 0 && (
                <dl className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 pt-3 border-t border-border">
                  {line.details.map((d) => (
                    <div
                      key={d.label}
                      className="flex items-center justify-between gap-3 py-0.5"
                    >
                      <dt className="text-xs text-muted-foreground">{d.label}</dt>
                      <dd className="text-xs font-medium text-foreground">
                        {d.value}
                      </dd>
                    </div>
                  ))}
                </dl>
              )}
              {line.key === "physicalDamage" && (
                <PhysicalDamageVehicleList policy={policy} />
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

function CoverageMetrics({ line }: { line: CoverageLine }) {
  if (line.metrics && line.metrics.length > 0) {
    return (
      <dl className="grid grid-cols-2 sm:grid-cols-3 gap-x-8 gap-y-3 text-sm">
        {line.metrics.map((m) => (
          <Metric key={m.label} label={m.label} value={m.value} />
        ))}
        {!line.premiumOnRight && (
          <Metric label="Premium" value={line.premium} accent />
        )}
      </dl>
    );
  }
  return (
    <dl className="grid grid-cols-2 sm:grid-cols-3 gap-x-8 gap-y-3 text-sm">
      <Metric label="Limit" value={line.limit} />
      <Metric label="Deductible" value={line.deductible} />
      {!line.premiumOnRight && (
        <Metric label="Premium" value={line.premium} accent />
      )}
    </dl>
  );
}

function Metric({
  label,
  value,
  accent = false,
}: {
  label: string;
  value: string;
  accent?: boolean;
}) {
  return (
    <div>
      <dt className="text-xs text-muted-foreground mb-0.5">{label}</dt>
      <dd
        className={cn(
          "font-semibold",
          accent ? "text-emerald-600" : "text-foreground"
        )}
      >
        {value}
      </dd>
    </div>
  );
}
